import { useEffect, useRef } from 'react';

// Background refresh for data that moves with the US trading day (issue #396): the stock
// table, the Strategies widget and the list performance badge. They refresh every
// REFRESH_MS on weekdays from 4:00 AM to 8:00 PM New York time (pre-market through
// after-hours) and make no periodic requests outside that window. Market holidays are
// not modelled; a holiday costs a few unchanged re-fetches, nothing more.
//
// TanStack's refetchInterval timer fires its fetch WITHOUT re-asking the interval, so the
// value returned must itself land on the window's edge: in the window it never runs past
// the close, and outside it is the exact wait until the next open. It is re-evaluated after
// every fetch and on every render, so a time-dependent value keeps pointing at the same
// instant rather than drifting.
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
export const REFRESH_MS = 30 * MINUTE;
const OPEN_MIN = 4 * 60;
const CLOSE_MIN = 20 * 60;

const NY_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

// New York calendar date and minutes since its midnight (fractional) at instant `ms`.
function nyClock(ms) {
  const p = Object.fromEntries(NY_PARTS.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return { y: +p.year, m: +p.month, d: +p.day, minutes: +p.hour * 60 + +p.minute + +p.second / 60 };
}

const isWeekday = (y, m, d) => { const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); return dow > 0 && dow < 6; };

// The UTC instant of a New York wall-clock time. Starts from an EST guess and corrects by the
// offset actually in force, so it is exact on both sides of a DST change (none happens at
// 4:00 AM, the only time asked for).
function nyWallToUtc(y, m, d, minutes) {
  const wall = Date.UTC(y, m - 1, d) + minutes * MINUTE;
  const wallAt = (ms) => { const c = nyClock(ms); return Date.UTC(c.y, c.m - 1, c.d) + c.minutes * MINUTE; };
  let t = wall + 5 * HOUR;
  for (let i = 0; i < 2; i++) t += wall - wallAt(t);
  return t;
}

// Milliseconds until the next background refresh is due, from instant `now`.
export function msUntilNextRefresh(now = Date.now()) {
  const c = nyClock(now);
  if (isWeekday(c.y, c.m, c.d) && c.minutes >= OPEN_MIN && c.minutes < CLOSE_MIN) {
    return Math.max(1000, Math.min(REFRESH_MS, Math.ceil((CLOSE_MIN - c.minutes) * MINUTE)));
  }
  for (let k = 0; k <= 7; k++) {
    const day = new Date(Date.UTC(c.y, c.m - 1, c.d + k));
    const [y, m, d] = [day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate()];
    if (!isWeekday(y, m, d) || (k === 0 && c.minutes >= OPEN_MIN)) continue;
    return Math.max(1000, nyWallToUtc(y, m, d, OPEN_MIN) - now);
  }
  return REFRESH_MS; // unreachable: a weekday always falls within the next 7 days
}

// For TanStack Query's `refetchInterval`, which calls it with the query as the argument.
// The default refetchIntervalInBackground = false already skips ticks while the tab is hidden.
export const marketHoursInterval = () => msUntilNextRefresh();

// The same schedule for data that is not a TanStack query (the stock table's loadData).
// Ticks are skipped while the tab is hidden; coming back to a tab whose last refresh is at
// least REFRESH_MS old refreshes at once, as refetchOnWindowFocus does for a stale query.
// A change of `resetKey` (e.g. switching lists, which loads fresh rows anyway) restarts the clock.
export function useMarketHoursRefresh(refresh, resetKey) {
  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  useEffect(() => {
    let timer;
    let last = Date.now();
    const run = () => { last = Date.now(); refreshRef.current(); };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (document.visibilityState === 'visible') run();
        schedule();
      }, msUntilNextRefresh());
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && Date.now() - last >= REFRESH_MS) {
        run();
        schedule();
      }
    };
    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [resetKey]);
}
